import React, { useState, useMemo } from "react"
import { useGetTestQuery } from "../../../app/services/meseeknaApiV2"

type SimulationPreviewModalProps = {
  testId: number
  onClose: () => void
}

type SimulationContents = {
  name?: string
  description?: string
  length?: string
  emergencyStart?: string
  emergencyEnd?: string
  emergencyCategory?: number
  allowPause?: boolean
  promptPlan?: boolean
  map?: {
    image?: { url?: string; ref?: string }
    plots?: Record<string, {
      name: string
      description?: string
      events?: Array<{
        id: number
        time: string
        trigger?: string
        messageType?: string
        description?: string
        prompt?: string
        responses?: Array<{
          id: number
          response: string
        }>
      }>
    }>
  }
  actionset?: {
    name?: string
    categories?: Array<{
      id: number
      text: string
      icon?: string
      categories?: Array<{
        id: number
        text: string
      }>
    }>
  }
  template?: {
    name?: string
  }
}

const SimulationPreviewModal: React.FC<SimulationPreviewModalProps> = ({
  testId,
  onClose,
}) => {
  const { data: testData, isLoading, error } = useGetTestQuery(testId)
  const [activeTab, setActiveTab] = useState<"overview" | "events" | "actions" | "map">("overview")

  const test = testData?.data
  const contents: SimulationContents | null = useMemo(() => {
    if (!test?.contents) return null
    try {
      return typeof test.contents === "string"
        ? JSON.parse(test.contents)
        : test.contents
    } catch {
      return null
    }
  }, [test?.contents])

  // Extract all events from all plots
  type SimulationEvent = {
    id: number
    time: string
    trigger?: string
    messageType?: string
    description?: string
    prompt?: string
    responses?: Array<{
      id: number
      response: string
    }>
  }

  const allEvents = useMemo(() => {
    if (!contents?.map?.plots) return []
    const events: Array<{
      plotName: string
      event: SimulationEvent
    }> = []

    Object.entries(contents.map.plots).forEach(([, plot]) => {
      if (plot.events) {
        plot.events.forEach((event) => {
          events.push({ plotName: plot.name, event })
        })
      }
    })

    // Sort by time
    events.sort((a, b) => {
      const timeA = a.event.time || "00:00"
      const timeB = b.event.time || "00:00"
      return timeA.localeCompare(timeB)
    })

    return events
  }, [contents])

  // Extract action categories
  const actionCategories = useMemo(() => {
    if (!contents?.actionset?.categories) return []
    return contents.actionset.categories
  }, [contents])

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !test) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl">
          <div className="text-center py-12">
            <p className="text-red-600">Failed to load simulation data</p>
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 bg-gray-200 rounded-md text-sm font-medium hover:bg-gray-300 cursor-pointer">
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{test.title}</h3>
            <p className="text-sm text-gray-500">{test.shortName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 cursor-pointer">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {(["overview", "events", "actions", "map"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 px-1 border-b-2 font-medium text-sm cursor-pointer transition-colors ${
                  activeTab === tab
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Image and basic info */}
              <div className="flex space-x-6">
                {test.imageData && (
                  test.imageData.startsWith("<svg") ? (
                    <div
                      className="h-32 w-32 rounded-lg overflow-hidden"
                      dangerouslySetInnerHTML={{ __html: test.imageData }}
                    />
                  ) : (
                    <img
                      src={test.imageData}
                      alt={test.title}
                      className="h-32 w-32 rounded-lg object-cover"
                    />
                  )
                )}
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-2">Basic Information</h4>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <dt className="text-gray-500">Type:</dt>
                    <dd className="text-gray-900">{test.simType || "Not assigned"}</dd>
                    <dt className="text-gray-500">Status:</dt>
                    <dd className="text-gray-900">{test.active === "Y" ? "Active" : "Inactive"}</dd>
                    <dt className="text-gray-500">Demo Mode:</dt>
                    <dd className="text-gray-900">{test.forDemo === "Y" ? "Yes" : "No"}</dd>
                    {contents?.length && (
                      <>
                        <dt className="text-gray-500">Duration:</dt>
                        <dd className="text-gray-900">{contents.length}</dd>
                      </>
                    )}
                    {contents?.emergencyStart && contents?.emergencyEnd && (
                      <>
                        <dt className="text-gray-500">Emergency Window:</dt>
                        <dd className="text-gray-900">
                          {contents.emergencyStart} - {contents.emergencyEnd}
                        </dd>
                      </>
                    )}
                  </dl>
                </div>
              </div>

              {/* Description */}
              {(test.manualDescription || contents?.description) && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                  <div
                    className="text-sm text-gray-600 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: test.manualDescription || contents?.description || "",
                    }}
                  />
                </div>
              )}

              {/* Links */}
              {(test.pdfURL || test.videoURL) && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Resources</h4>
                  <div className="flex space-x-4">
                    {test.pdfURL && (
                      <a
                        href={test.pdfURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 text-sm">
                        View PDF Guide
                      </a>
                    )}
                    {test.videoURL && (
                      <a
                        href={test.videoURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 text-sm">
                        Watch Video
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Raw JSON preview */}
              {!contents && test.contents && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Raw JSON (Parse Error)</h4>
                  <pre className="bg-gray-100 p-4 rounded-md text-xs overflow-x-auto max-h-64">
                    {test.contents.substring(0, 1000)}...
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Events Tab */}
          {activeTab === "events" && (
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">
                Timeline Events ({allEvents.length})
              </h4>
              {allEvents.length === 0 ? (
                <p className="text-gray-500 text-sm">No events found in simulation</p>
              ) : (
                <div className="space-y-3">
                  {allEvents.map(({ plotName, event }, idx) => (
                    <div
                      key={`${event.id}-${idx}`}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-mono bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded">
                              {event.time || "00:00"}
                            </span>
                            <span className="text-xs text-gray-500">{plotName}</span>
                            {event.messageType && (
                              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                                {event.messageType}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-900 mt-1">
                            {event.description || event.prompt || "No description"}
                          </p>
                        </div>
                      </div>
                      {event.responses && event.responses.length > 0 && (
                        <div className="mt-2 pl-4 border-l-2 border-gray-300">
                          <p className="text-xs text-gray-500 mb-1">Responses:</p>
                          {event.responses.map((response) => (
                            <p key={response.id} className="text-xs text-gray-700">
                              • {response.response}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Actions Tab */}
          {activeTab === "actions" && (
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">
                Action Categories ({actionCategories.length})
              </h4>
              {actionCategories.length === 0 ? (
                <p className="text-gray-500 text-sm">No action categories found</p>
              ) : (
                <div className="space-y-3">
                  {actionCategories.map((category) => (
                    <div
                      key={category.id}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center space-x-2 mb-2">
                        {category.icon && (
                          <span className="text-lg">{category.icon}</span>
                        )}
                        <span className="font-medium text-gray-900">
                          {category.text}
                        </span>
                      </div>
                      {category.categories && category.categories.length > 0 && (
                        <div className="pl-4 space-y-1">
                          {category.categories.map((subcat) => (
                            <div key={subcat.id} className="text-sm text-gray-600">
                              • {subcat.text}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Map Tab */}
          {activeTab === "map" && (
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">Map & Plots</h4>
              {contents?.map?.image?.url ? (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Map Image:</p>
                  <img
                    src={contents.map.image.url}
                    alt="Simulation Map"
                    className="max-w-full h-auto rounded-lg border border-gray-200"
                  />
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No map image available</p>
              )}

              {contents?.map?.plots && Object.keys(contents.map.plots).length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">
                    Plots ({Object.keys(contents.map.plots).length}):
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(contents.map.plots).map(([key, plot]) => (
                      <div
                        key={key}
                        className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="font-medium text-sm text-gray-900">
                          {plot.name}
                        </div>
                        {plot.description && (
                          <p className="text-xs text-gray-500 mt-1">
                            {plot.description}
                          </p>
                        )}
                        {plot.events && (
                          <p className="text-xs text-indigo-600 mt-1">
                            {plot.events.length} event{plot.events.length !== 1 ? "s" : ""}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 cursor-pointer">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default SimulationPreviewModal
